import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getGetDoclistQueryKey } from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { getListDocumentsQueryKey } from "~/api/default/default";
import { useArchiveDocument } from "~/api/documents/documents";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

interface ArchiveDocumentAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  documentName: string;
  caseId: string;
}

export const ArchiveDocumentAlertDialog: FC<
  ArchiveDocumentAlertDialogProps
> = ({ open, setOpen, documentId, documentName, caseId }) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: archiveDocument } = useArchiveDocument();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();
  const [isFinalizing, setIsFinalizing] = useState(false);

  const onArchiveDocument = async () => {
    try {
      setIsFinalizing(true);
      await archiveDocument({
        organizationId,
        projectId: caseId,
        documentId,
      });
      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      await queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });

      setOpen(false);
    } catch (error: any) {
      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error" }),
        description: intl.formatMessage({
          id: "archiveDocument.failed",
          defaultMessage: "Failed to archive the document. Please try again.",
        }),
        variant: "destructive",
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage id="caseDocument.archiveDialog.title" />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="caseDocument.archiveDialog.description"
              defaultMessage='{docName} will be archived. Document will be moved to "Archived" in your account settings.'
              values={{
                docName: <strong>{documentName}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => setOpen(false)}
            disabled={isFinalizing}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={onArchiveDocument}
            disabled={isFinalizing}
            aria-busy={isFinalizing}
          >
            {isFinalizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage id="archive" defaultMessage="Archive" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
